import type {ReactElement} from 'react';
import {useState} from 'react';
import type {AspectRatioPopupProps} from './aspect-ratio-popup.types';
import {ASPECT_RATIO_AUTO_VALUE, ASPECT_RATIO_GROUP_KEY, ORIENTATION_GROUP_KEY, ORIENTATION_ITEMS} from './aspect-ratio-popup.types';
import styles from './aspect-ratio-popup.module.scss';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {MenuGroupList} from '@Components/ai-prompt-box/components/menu-group-list/menu-group-list';
import type {ContextualMenuGroup, ContextualMenuItemWithIcon} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {ContextualMenuGroupType, ContextualMenuItemGraphicType} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {AspectRatioOrientation} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector.types';
import {ASPECT_RATIO_OPTIONS} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector.types';
import {getIconForAspectRatio, getOrientationForAspectRatio, parseAspectRatio} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector.library';

export function AspectRatioPopup({pinnedAspectRatio, onRatioSelect}: AspectRatioPopupProps): ReactElement {
  const [orientation, setOrientation] = useState<AspectRatioOrientation>(() => getOrientationForAspectRatio(pinnedAspectRatio));

  const getOrientationGroup = (): ContextualMenuGroup => {
    return {
      label: window.i18next.t('pmwjs_orientation'),
      key: ORIENTATION_GROUP_KEY,
      type: ContextualMenuGroupType.PILLS,
      items: ORIENTATION_ITEMS.map((orientationItem) => ({
        label: window.i18next.t(orientationItem.labelKey),
        value: orientationItem.orientation,
        active: orientation === orientationItem.orientation,
      })),
    };
  };

  const getRatioGroup = (): ContextualMenuGroup => {
    const autoItem: ContextualMenuItemWithIcon = {
      label: window.i18next.t('pmwjs_auto'),
      value: ASPECT_RATIO_AUTO_VALUE,
      active: pinnedAspectRatio === null,
      graphicType: ContextualMenuItemGraphicType.ICON,
      icon: getIconForAspectRatio(null),
    };
    const ratioItems: ContextualMenuItemWithIcon[] = ASPECT_RATIO_OPTIONS[orientation].map((ratio) => ({
      label: ratio,
      value: ratio,
      active: pinnedAspectRatio === ratio,
      graphicType: ContextualMenuItemGraphicType.ICON,
      icon: getIconForAspectRatio(ratio),
    }));

    return {
      label: window.i18next.t('pmwjs_aspect_ratio'),
      key: ASPECT_RATIO_GROUP_KEY,
      type: ContextualMenuGroupType.PILLS,
      items: [autoItem, ...ratioItems],
    };
  };

  const groups = [getOrientationGroup(), getRatioGroup()];

  const handleItemClick = (groupIndex: number, itemIndex: number): void => {
    const group = groups[groupIndex];
    const item = group.items[itemIndex];

    if (group.key === ORIENTATION_GROUP_KEY) {
      setOrientation(ORIENTATION_ITEMS[itemIndex].orientation);
      return;
    }

    onRatioSelect(parseAspectRatio(item.value));
  };

  const getResetButton = (): ReactElement | null => {
    if (pinnedAspectRatio === null) {
      return null;
    }

    return (
      <Button
        customClasses={styles.resetButton}
        type={ButtonType.PRIMARY}
        size={ButtonSize.SMALL}
        text={window.i18next.t('pmwjs_reset_to_default')}
        onClick={(): void => {
          onRatioSelect(null);
        }}
      />
    );
  };

  return (
    <div className={styles.popupContent}>
      <div className={styles.sectionsGroup}>
        <MenuGroupList groups={groups} onItemClick={handleItemClick} />
      </div>
      {getResetButton()}
    </div>
  );
}
